import React, { useState } from "react"
import { useAuthFlowExecution } from "./use_auth_flow_execution"
import { StepCard } from "./step_card"

interface Props {
  channelModuleName: string
  callbackParams?: Record<string, string> | null
}

// --- Sub-components for top-level UI sections ---

interface TestControlsProps {
  hasStarted: boolean
  flowCompleted: boolean
  onStart: () => void
  onReset: () => void
}

function TestControls({
  hasStarted,
  flowCompleted,
  onStart,
  onReset,
}: TestControlsProps) {
  const showStart = !hasStarted && !flowCompleted
  const showReset = hasStarted || flowCompleted

  return (
    <div className="auth-test-controls">
      {showStart && (
        <button className="button-tertiary" onClick={onStart}>
          Begin test
        </button>
      )}
      {showReset && (
        <button className="button-tertiary" onClick={onReset}>
          Reset test
        </button>
      )}
    </div>
  )
}

interface ContextDisplayProps {
  context: Record<string, unknown>
  editing: boolean
  editValue: string
  onEditValueChange: (value: string) => void
  onSave: () => boolean
  onCancel: () => void
  onEditContext: () => void
}

function ContextDisplay({
  context,
  editing,
  editValue,
  onEditValueChange,
  onSave,
  onCancel,
  onEditContext,
}: ContextDisplayProps) {
  const [expanded, setExpanded] = useState(false)

  const handleSave = () => {
    if (!onSave()) {
      alert("Invalid JSON")
    }
  }

  const toggleExpanded = () => setExpanded(prev => !prev)
  const expandClass = expanded ? "expanded" : "collapsed"

  return (
    <div className={`publish-item auth-test-context ${expandClass}`} onClick={toggleExpanded}>
      <header>Current execution context</header>
      {expanded && (
        <div className="auth-test-section-content" onClick={e => e.stopPropagation()}>
          {editing ? (
            <>
              <textarea
                className="auth-test-context-editor"
                value={editValue}
                onChange={e => onEditValueChange((e.target as HTMLTextAreaElement).value)}
              />
              <div className="auth-test-editor-actions">
                <button className="button-tertiary" onClick={handleSave}>
                  Save
                </button>
                <button className="button-tertiary" onClick={onCancel}>
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <pre className="auth-test-code-block">
                {Object.keys(context).length > 0 ? JSON.stringify(context, null, 2) : "(empty)"}
              </pre>
              <div className="auth-test-editor-actions">
                <button className="button-tertiary" onClick={onEditContext}>
                  Edit context
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

interface FlowStatusBannerProps {
  status: "passing" | "failing"
}

function FlowStatusBanner({ status }: FlowStatusBannerProps) {
  const isPassing = status === "passing"
  const title = isPassing
    ? "Authentication flow completed successfully"
    : "Authentication flow failed"
  const message = isPassing
    ? "All steps executed successfully. Final context contains the authentication result."
    : "One or more steps failed. Check the step details for more information."

  return (
    <div className={`publish-item heading ${status}`}>
      <header>{title}</header>
      <div className="auth-test-section-content">
        <p>{message}</p>
      </div>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="publish-item">
      <header>Loading authentication flow steps...</header>
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="publish-item failing">
      <header>{message}</header>
    </div>
  )
}

function EmptyState() {
  return (
    <div className="publish-item">
      <header>No authentication flow steps configured</header>
    </div>
  )
}

// --- Main component ---

const ModularAuthSteps = ({ channelModuleName, callbackParams }: Props) => {
  const {
    steps,
    loading,
    loadError,
    context,
    flowCompleted,
    resetCount,
    currentStepIndex,
    isExecuting,
    getStepState,
    startTest,
    reset,
    runStep,
    retryStep,
    submitForm,
    editingContext,
    contextEditValue,
    setContextEditValue,
    startEditingContext,
    saveContext,
    cancelEditingContext,
  } = useAuthFlowExecution({ channelModuleName, callbackParams })

  if (loading) return <LoadingState />
  if (loadError) return <ErrorState message={loadError} />
  if (steps.length === 0) return <EmptyState />

  const hasStarted = currentStepIndex !== null || flowCompleted || steps.some(s => getStepState(s.id).status !== "pending")
  const hasFailed = steps.some(s => getStepState(s.id).status === "failed")
  const flowStatus: "passing" | "failing" | null = flowCompleted ? "passing" : hasFailed ? "failing" : null

  return (
    <>
      {flowStatus && <FlowStatusBanner status={flowStatus} />}

      <ContextDisplay
        key={`context-${resetCount}`}
        context={context}
        editing={editingContext}
        editValue={contextEditValue}
        onEditValueChange={setContextEditValue}
        onSave={saveContext}
        onCancel={cancelEditingContext}
        onEditContext={startEditingContext}
      />

      <TestControls
        hasStarted={hasStarted}
        flowCompleted={flowCompleted}
        onStart={startTest}
        onReset={reset}
      />

      {steps.map((step, index) => (
        <StepCard
          key={`${step.id}-${resetCount}`}
          step={step}
          stepIndex={index}
          state={getStepState(step.id)}
          currentStepIndex={currentStepIndex}
          isExecuting={isExecuting}
          onRun={runStep}
          onRetry={retryStep}
          onFormSubmit={submitForm}
        />
      ))}
    </>
  )
}

export default ModularAuthSteps
